import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Paginator } from '@bindoc-projects/platform-lib/data';
import {
  BdAdminPermissionService,
  BdPermissionsCompanyAdminService,
  BdShareablesService,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import { BdCompany } from '@bindoc-projects/resource-domain-lib';
import { AvatarModule } from 'primeng/avatar';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { IftaLabel } from 'primeng/iftalabel';
import { take } from 'rxjs';

@Component({
  selector: 'bd-company-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-iftalabel>
      <p-autocomplete
        [suggestions]="companies()"
        [forceSelection]="true"
        dataKey="id"
        optionLabel="name"
        appendTo="body"
        fluid
        variant="filled"
        placeholder="Kunde suchen..."
        (completeMethod)="onComplete($event)"
        (onSelect)="onSelect($event)"
      >
        <ng-template let-company #item>
          <div class="flex items-center gap-2">
            <p-avatar [label]="initialsOf(company)" shape="circle"></p-avatar>
            <span>{{ company.name }}</span>
          </div>
        </ng-template>
      </p-autocomplete>
      <label>Kunde</label>
    </p-iftalabel>
  `,
  imports: [FormsModule, AutoComplete, IftaLabel, AvatarModule],
})
export class BdCompanySelectComponent implements OnInit {
  public readonly companySelect = output<BdCompany>();

  public readonly companies = signal<BdCompany[]>([]);

  private static readonly PAGE_SIZE = 20;

  private readonly _adminCompanyService = inject(
    BdPermissionsCompanyAdminService,
  );
  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _adminPermissions = inject(BdAdminPermissionService);

  private readonly _isAdmin = signal(false);

  ngOnInit(): void {
    this._adminPermissions
      .containsPermission(['ADMIN:GROUPS'])
      .pipe(take(1))
      .subscribe((hasAdmin) => {
        this._isAdmin.set(hasAdmin);
        if (!hasAdmin) {
          // Seed with the first page; typing narrows it server-side.
          this._resourcesService
            .searchMyCompanies(0, BdCompanySelectComponent.PAGE_SIZE)
            .pipe(take(1))
            .subscribe((page) => this.companies.set(page.content));
        }
      });
  }

  public onComplete(event: AutoCompleteCompleteEvent): void {
    const query = event.query;
    if (this._isAdmin()) {
      this._adminCompanyService
        .search(Paginator.of({ pageSize: 20, query }))
        .subscribe((page) => this.companies.set(page.content));
    } else {
      this._resourcesService
        .searchMyCompanies(0, BdCompanySelectComponent.PAGE_SIZE, query)
        .pipe(take(1))
        .subscribe((page) => this.companies.set(page.content));
    }
  }

  public onSelect(event: AutoCompleteSelectEvent): void {
    this.companySelect.emit(event.value as BdCompany);
  }

  public initialsOf(company: BdCompany): string {
    return company.name
      .split(/\s+/)
      .filter((p) => p.length > 0)
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join('');
  }
}
